import React from 'react';
import { Star, TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';
import LivePrice from './LivePrice';

/**
 * Sticky top panel showing the user's favorites with sub-15s live price,
 * recommendation, and recent TP/SL level crossings. Populated by the WS
 * "favorite_update" messages pushed every 10s by the backend favorites loop.
 */
export default function FavoritesLivePanel({ favorites, levelEvents, lastUpdate, onSelect, watchlist, tickPrices }) {
    // Only show favorites that are in the user's watchlist
    const myFavs = (favorites || []).filter((f) => watchlist?.includes(f.symbol));
    const ageSec = lastUpdate ? Math.max(0, Math.floor((Date.now() - lastUpdate * 1000) / 1000)) : null;

    if (myFavs.length === 0) {
        return (
            <div
                data-testid="favorites-live-empty"
                className="border border-amber-500/15 bg-gradient-to-r from-amber-950/10 to-slate-900 rounded-md px-4 py-3 flex items-center gap-3"
            >
                <Star className="h-4 w-4 text-amber-400/70" />
                <span className="text-[11px] text-amber-300/80 uppercase tracking-wider">Canlı Favoriler</span>
                <span className="text-xs text-slate-400">
                    · Henüz favori eklemediniz. Tablodan ⭐ ikonuna tıklayarak coin ekleyebilirsiniz — sistem 10 saniyede bir derin tarama yapar.
                </span>
            </div>
        );
    }

    return (
        <div
            data-testid="favorites-live-panel"
            className="border border-amber-500/25 bg-gradient-to-r from-amber-950/15 via-slate-900 to-slate-800 rounded-md overflow-hidden"
        >
            <div className="px-4 py-2 border-b border-amber-500/10 flex items-center gap-2">
                <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-amber-300 font-medium">
                    Canlı Favoriler · 10 sn tarama
                </span>
                {ageSec !== null && (
                    <span className="text-[10px] text-emerald-400/80">
                        ● {ageSec}sn önce
                    </span>
                )}
                <span className="ml-auto text-[10px] text-slate-400">{myFavs.length} coin</span>
            </div>

            {levelEvents && levelEvents.length > 0 && (
                <div className="px-4 py-2 border-b border-amber-500/10 bg-amber-500/5 flex flex-wrap gap-2">
                    {levelEvents.slice(-3).map((ev) => (
                        <div
                            key={`${ev.symbol}-${ev.level}-${ev.crossed_at}-${ev.price}`}
                            data-testid={`level-event-${ev.symbol}-${ev.level}`}
                            className="flex items-center gap-1.5 text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-sm px-2 py-1"
                        >
                            <AlertCircle className="h-3 w-3" />
                            <span className="font-mono font-semibold">{ev.symbol.replace('USDT', '')}</span>
                            <span>{ev.level} seviyesine değdi</span>
                            {ev.short_id && <span className="font-mono text-amber-300" data-testid={`level-event-id-${ev.symbol}-${ev.level}`}>#{ev.short_id}</span>}
                            <LivePrice symbol={ev.symbol} tick={tickPrices?.[ev.symbol]} fallbackPrice={ev.price} className="text-amber-100" />
                        </div>
                    ))}
                </div>
            )}

            <div className="divide-y divide-slate-700/50">
                {myFavs.map((r) => {
                    const rec = r.recommendation || {};
                    const action = rec.action || 'WAIT';
                    const Icon = action === 'LONG' ? TrendingUp : action === 'SHORT' ? TrendingDown : Minus;
                    const color = action === 'LONG' ? 'text-emerald-300' : action === 'SHORT' ? 'text-rose-300' : 'text-slate-400';
                    const tick = tickPrices?.[r.symbol];
                    const chg = tick?.change_pct_24h ?? r.change_pct_24h ?? 0;
                    return (
                        <button
                            key={r.symbol}
                            onClick={() => onSelect?.(r.symbol)}
                            data-testid={`fav-live-${r.symbol}`}
                            className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-amber-500/5 transition-colors text-left"
                        >
                            <div className="w-24 flex items-center gap-1.5">
                                <Icon className={`h-3.5 w-3.5 ${color}`} />
                                <span className="font-mono font-semibold text-slate-100 text-sm">
                                    {r.symbol.replace('USDT', '')}
                                </span>
                            </div>

                            <div className="w-28">
                                <LivePrice symbol={r.symbol} tick={tick} fallbackPrice={r.price} className="text-slate-100 text-sm" />
                            </div>

                            <div className={`w-20 font-mono text-xs ${chg >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                {chg >= 0 ? '+' : ''}{chg.toFixed(2)}%
                            </div>

                            <div className="flex-1 text-[11px] text-slate-300 truncate">
                                {r.summary}
                            </div>

                            {action !== 'WAIT' && (
                                <div className="flex items-center gap-2">
                                    <span className={`text-[11px] font-mono ${color}`}>
                                        {action === 'LONG' ? 'LONG' : 'SHORT'}
                                    </span>
                                    <span className="text-[10px] font-mono bg-slate-600 text-slate-100 px-1.5 py-0.5 rounded-sm">
                                        {rec.leverage?.label || '—'}
                                    </span>
                                    <span className={`text-[11px] font-mono ${color}`}>
                                        %{rec.confidence}
                                    </span>
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
